import { supabase } from './supabase'

const BUCKET_NAME = 'pet-photos'

/**
 * Fetch pet avatar from pet_photos table
 * @param {string} petId - UUID of the pet
 * @returns {Promise<Object|null>} Pet photo record or null
 */
export async function fetchPetAvatar(petId) {
	if (!petId) return null

	const { data, error } = await supabase
		.from('pet_photos')
		.select('*')
		.eq('pet_id', petId)
		.eq('is_avatar', true)
		.maybeSingle()

	if (error) {
		console.error('Error fetching pet avatar:', error)
	}

	return data || null
}

/**
 * Batch fetch pet avatars for multiple pets in a single DB call
 * @param {string[]} petIds - Array of pet UUIDs
 * @returns {Promise<Object>} Map of petId -> avatar URL (only includes pets with avatars)
 */
export async function fetchPetAvatarsBatch(petIds) {
	if (!petIds || petIds.length === 0) return {}

	const { data, error } = await supabase
		.from('pet_photos')
		.select('*')
		.in('pet_id', petIds)
		.eq('is_avatar', true)

	if (error) {
		console.error('Error batch fetching pet avatars:', error)
		return {}
	}

	// Build map of petId -> avatar URL
	const avatarMap = {}
	if (data) {
		data.forEach(photo => {
			const url = getAvatarUrl(photo)
			if (url) {
				avatarMap[photo.pet_id] = url
			}
		})
	}

	return avatarMap
}

/**
 * Get public URL for a pet photo from Supabase storage
 * @param {Object} petPhoto - Pet photo record with bucket and path
 * @returns {string|null} Public URL or null
 */
export function getAvatarUrl(petPhoto) {
	if (!petPhoto || !petPhoto.bucket || !petPhoto.path) return null

	const { data } = supabase.storage
		.from(petPhoto.bucket)
		.getPublicUrl(petPhoto.path)

	if (!data?.publicUrl) return null

	// Add cache-busting parameter using updated_at timestamp to force refresh after re-upload
	const cacheBuster = petPhoto.updated_at
		? `?t=${new Date(petPhoto.updated_at).getTime()}`
		: ''

	return data.publicUrl + cacheBuster
}

/**
 * Upload pet avatar to storage and create/update database record
 * @param {string} petId - UUID of the pet
 * @param {string} merchantId - UUID of the merchant
 * @param {string} clientId - UUID of the client (optional)
 * @param {Blob} imageBlob - JPEG image blob to upload
 * @param {string|null} existingPhotoId - Existing photo record ID for re-upload
 * @returns {Promise<{photo: Object, url: string}|null>} Photo record and URL or null on error
 */
export async function uploadPetAvatar(petId, merchantId, clientId, imageBlob, existingPhotoId = null) {
	try {
		let photoId = existingPhotoId
		let photoRecord = null

		if (existingPhotoId) {
			// Re-upload: delete old file and update record
			const { data: existingPhoto, error: fetchError } = await supabase
				.from('pet_photos')
				.select('*')
				.eq('id', existingPhotoId)
				.single()

			if (fetchError) {
				console.error('Error fetching existing photo:', fetchError)
				throw fetchError
			}

			// Delete old file from storage
			if (existingPhoto.path) {
				const { error: deleteError } = await supabase.storage
					.from(existingPhoto.bucket)
					.remove([existingPhoto.path])

				if (deleteError) {
					console.error('Error deleting old file:', deleteError)
					// Continue anyway - file might not exist
				}
			}

			photoId = existingPhotoId
			photoRecord = existingPhoto
		} else {
			// Create new record in pet_photos table
			const { data: newPhoto, error: insertError } = await supabase
				.from('pet_photos')
				.insert({
					pet_id: petId,
					merchant_id: merchantId,
					client_id: clientId || null,
					bucket: BUCKET_NAME,
					is_avatar: true,
					path: '', // Will update after upload
					description: 'Pet avatar'
				})
				.select()
				.single()

			if (insertError) {
				console.error('Error creating photo record:', insertError)
				throw insertError
			}

			photoId = newPhoto.id
			photoRecord = newPhoto
		}

		// Upload to storage: {petId}/{photoId}.jpeg
		const filePath = `${petId}/${photoId}.jpeg`

		const { error: uploadError } = await supabase.storage
			.from(BUCKET_NAME)
			.upload(filePath, imageBlob, {
				contentType: 'image/jpeg',
				upsert: true
			})

		if (uploadError) {
			console.error('Error uploading to storage:', uploadError)
			// Clean up DB record if new
			if (!existingPhotoId) {
				await supabase.from('pet_photos').delete().eq('id', photoId)
			}
			throw uploadError
		}

		// Update record with path and updated_at
		const { data: updatedPhoto, error: updateError } = await supabase
			.from('pet_photos')
			.update({
				path: filePath,
				updated_at: new Date().toISOString()
			})
			.eq('id', photoId)
			.select()
			.single()

		if (updateError) {
			console.error('Error updating photo record:', updateError)
			throw updateError
		}

		const url = getAvatarUrl(updatedPhoto)

		return {
			photo: updatedPhoto,
			url
		}
	} catch (error) {
		console.error('uploadPetAvatar error:', error)
		return null
	}
}

/**
 * Delete pet avatar from storage and database
 * @param {string} photoId - UUID of the pet_photos record
 * @returns {Promise<boolean>} Success status
 */
export async function deletePetAvatar(photoId) {
	try {
		// Fetch the photo record
		const { data: photo, error: fetchError } = await supabase
			.from('pet_photos')
			.select('*')
			.eq('id', photoId)
			.single()

		if (fetchError) {
			console.error('Error fetching photo for deletion:', fetchError)
			return false
		}

		// Delete from storage
		if (photo.path) {
			const { error: deleteStorageError } = await supabase.storage
				.from(photo.bucket)
				.remove([photo.path])

			if (deleteStorageError) {
				console.error('Error deleting from storage:', deleteStorageError)
				// Continue to delete DB record anyway
			}
		}

		// Delete from database
		const { error: deleteDbError } = await supabase
			.from('pet_photos')
			.delete()
			.eq('id', photoId)

		if (deleteDbError) {
			console.error('Error deleting photo record:', deleteDbError)
			return false
		}

		return true
	} catch (error) {
		console.error('deletePetAvatar error:', error)
		return false
	}
}

/**
 * Delete all photos for a specific pet from storage and database
 * @param {string} petId - UUID of the pet
 * @returns {Promise<boolean>} Success status
 */
export async function deleteAllPetPhotos(petId) {
	if (!petId) return true

	try {
		// Fetch all photos for this pet
		const { data: photos, error: fetchError } = await supabase
			.from('pet_photos')
			.select('*')
			.eq('pet_id', petId)

		if (fetchError) {
			console.error('Error fetching pet photos for deletion:', fetchError)
			return false
		}

		if (!photos || photos.length === 0) {
			return true // No photos to delete
		}

		// Collect all storage paths to delete
		const pathsToDelete = photos
			.filter(photo => photo.path)
			.map(photo => photo.path)

		// Delete from storage (batch delete)
		if (pathsToDelete.length > 0) {
			const { error: deleteStorageError } = await supabase.storage
				.from(BUCKET_NAME)
				.remove(pathsToDelete)

			if (deleteStorageError) {
				console.error('Error deleting pet photos from storage:', deleteStorageError)
				// Continue to delete DB records anyway
			}
		}

		// Delete all records from database
		const { error: deleteDbError } = await supabase
			.from('pet_photos')
			.delete()
			.eq('pet_id', petId)

		if (deleteDbError) {
			console.error('Error deleting pet photo records:', deleteDbError)
			return false
		}

		return true
	} catch (error) {
		console.error('deleteAllPetPhotos error:', error)
		return false
	}
}

/**
 * Delete all photos for all pets belonging to a client
 * @param {string} clientId - UUID of the client
 * @returns {Promise<boolean>} Success status
 */
export async function deleteAllClientPetPhotos(clientId) {
	if (!clientId) return true

	try {
		// Fetch all photos for this client's pets
		const { data: photos, error: fetchError } = await supabase
			.from('pet_photos')
			.select('*')
			.eq('client_id', clientId)

		if (fetchError) {
			console.error('Error fetching client pet photos for deletion:', fetchError)
			return false
		}

		if (!photos || photos.length === 0) {
			return true // No photos to delete
		}

		// Collect all storage paths to delete
		const pathsToDelete = photos
			.filter(photo => photo.path)
			.map(photo => photo.path)

		// Delete from storage (batch delete)
		if (pathsToDelete.length > 0) {
			const { error: deleteStorageError } = await supabase.storage
				.from(BUCKET_NAME)
				.remove(pathsToDelete)

			if (deleteStorageError) {
				console.error('Error deleting client pet photos from storage:', deleteStorageError)
				// Continue to delete DB records anyway
			}
		}

		// Delete all records from database
		const { error: deleteDbError } = await supabase
			.from('pet_photos')
			.delete()
			.eq('client_id', clientId)

		if (deleteDbError) {
			console.error('Error deleting client pet photo records:', deleteDbError)
			return false
		}

		return true
	} catch (error) {
		console.error('deleteAllClientPetPhotos error:', error)
		return false
	}
}

